import type { UserRole, AdviserRole } from '../types';
import { routes } from '../routes';

/** Finance reports (debtors, commissions, bordeaux, etc.) */
export function canViewReports(role?: UserRole): boolean {
  return role === 'FINANCE_ADMIN' || role === 'SUPER_ADMIN';
}

/** Business-wide invoice access (all invoices in the FAP) */
export function hasBusinessWideInvoiceAccess(user?: {
  role?: UserRole;
} | null): boolean {
  if (!user?.role) return false;
  return (
    user.role === 'BUSINESS_ADMIN' ||
    user.role === 'FINANCE_ADMIN' ||
    user.role === 'SUPER_ADMIN'
  );
}

/** Invoice list/detail read access (business-wide for admins, scoped for quoting advisers) */
export function canViewInvoices(user?: {
  role?: UserRole;
  adviserRole?: AdviserRole | null;
} | null): boolean {
  if (!user?.role) return false;
  if (hasBusinessWideInvoiceAccess(user)) return true;
  return user.role === 'ADVISER' && user.adviserRole === 'QUOTING_ADVISER';
}

/** Mark paid, payments, write-offs, reconciliation */
export function canManageInvoicePayments(role?: UserRole): boolean {
  return role === 'FINANCE_ADMIN' || role === 'SUPER_ADMIN';
}

/** Void (soft-delete) invoice transactions */
export function canVoidInvoiceTransactions(role?: UserRole): boolean {
  return role === 'FINANCE_ADMIN' || role === 'SUPER_ADMIN';
}

/** Quotes, policies, renewals, clients — create/edit/servicing */
export function canAccessOperationalAreas(role?: UserRole): boolean {
  return (
    role === 'ADVISER' ||
    role === 'BUSINESS_ADMIN' ||
    role === 'SUPER_ADMIN'
  );
}

/** Change quote servicing adviser (internal advisers and admins only) */
export function canChangeQuoteServicingAdviser(user?: {
  role?: UserRole;
  adviserRole?: AdviserRole | null;
} | null): boolean {
  if (!user?.role) return false;
  if (user.role === 'BUSINESS_ADMIN' || user.role === 'SUPER_ADMIN') return true;
  return user.role === 'ADVISER' && user.adviserRole === 'INTERNAL_ADVISER';
}

/** Edit quote admin fee (admins only; advisers cannot change) */
export function canEditQuoteAdminFee(user?: {
  role?: UserRole;
  adviserRole?: AdviserRole | null;
} | null): boolean {
  if (!user?.role) return false;
  return user.role === 'BUSINESS_ADMIN' || user.role === 'SUPER_ADMIN';
}

/** Edit quote broker fee (internal advisers and admins; quoting advisers cannot change) */
export function canEditQuoteBrokerFee(user?: {
  role?: UserRole;
  adviserRole?: AdviserRole | null;
} | null): boolean {
  if (!user?.role) return false;
  if (user.role === 'BUSINESS_ADMIN' || user.role === 'SUPER_ADMIN') return true;
  return user.role === 'ADVISER' && user.adviserRole === 'INTERNAL_ADVISER';
}

/** Cancel a policy (internal advisers and admins; external advisers cannot cancel) */
export function canCancelPolicies(user?: {
  role?: UserRole;
  adviserRole?: AdviserRole | null;
} | null): boolean {
  if (!user?.role) return false;
  if (user.role === 'BUSINESS_ADMIN' || user.role === 'SUPER_ADMIN') return true;
  return user.role === 'ADVISER' && user.adviserRole === 'INTERNAL_ADVISER';
}

/** Record a cancellation already processed in Ando (admins and internal advisers) */
export function canManuallyCancelPolicy(user?: {
  role?: UserRole;
  adviserRole?: AdviserRole | null;
} | null): boolean {
  if (!user?.role) return false;
  if (user.role === 'BUSINESS_ADMIN' || user.role === 'SUPER_ADMIN') return true;
  return user.role === 'ADVISER' && user.adviserRole === 'INTERNAL_ADVISER';
}

/** Read-only access to clients, quotes, policies, and renewals */
export function canViewOperationalAreas(role?: UserRole): boolean {
  return canAccessOperationalAreas(role) || role === 'FINANCE_ADMIN';
}

/** Client profile read access (e.g. from invoice links); finance is read-only */
export function canViewClientProfile(role?: UserRole): boolean {
  return canViewOperationalAreas(role);
}

/** Policy detail read access (e.g. from invoice links); finance is read-only */
export function canViewPolicyDetail(role?: UserRole): boolean {
  return canViewOperationalAreas(role);
}

/** Policy commission breakdown on policy detail page */
export function canViewPolicyCommissionDetails(role?: UserRole): boolean {
  return role === 'SUPER_ADMIN';
}

/** Users / FAP management pages */
export function canManageUsers(role?: UserRole): boolean {
  return role === 'BUSINESS_ADMIN' || role === 'SUPER_ADMIN';
}

/** Create SUPER_ADMIN, FINANCE_ADMIN, or BUSINESS_ADMIN */
export function canCreateAdminAccounts(role?: UserRole): boolean {
  return role === 'SUPER_ADMIN';
}

/** Create ADVISER users */
export function canCreateAdviser(role?: UserRole): boolean {
  return role === 'BUSINESS_ADMIN' || role === 'SUPER_ADMIN';
}

export function getRoleDisplayName(role?: UserRole): string {
  switch (role) {
    case 'SUPER_ADMIN':
      return 'Super Admin';
    case 'BUSINESS_ADMIN':
      return 'Business Admin';
    case 'FINANCE_ADMIN':
      return 'Finance Admin';
    case 'ADVISER':
      return 'Adviser';
    default:
      return 'Adviser';
  }
}

export function getDefaultLandingPath(role?: UserRole): string {
  if (role === 'FINANCE_ADMIN') {
    return routes.reports;
  }
  return routes.dashboard;
}
